import * as React from "react";
import { styled } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import CloseIcon from "@mui/icons-material/Close";
import Heading from "../../../../component-lib/Atoms/heading/EventEHRHeading";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import { CREATE_EMS_SCENE } from "./verifyConstant";
import { Form, Formik } from "formik";
import Input from "../../../../component-lib/Atoms/Input/Input";
import moment from "moment";
import Button from "../../../../component-lib/Molecules/button/Button";
import {
  hideLoader,
  showLoader,
} from "../../../../component-lib/Molecules/loader/LoaderSlice";
import {
  getLazyLoadingApiResponse,
  getLazyLoadingSearchApiResponse,
} from "../../../../utils/constantFunctions";
import EventEHRLazyDropdown from "../../../../component-lib/Molecules/lazy-loading-search/EventEHRLazyLoadingSearch";
import { searchIcon } from "../../../../assets";
import { convertPartToPatient, createEMSVisit } from "../../patientSaga";
import { componentKey } from "../../patientSlice";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
  "& .MuiPaper-root": {
    width: "708px", // Set your custom width here
    maxWidth: "none", // Prevents Material-UI from overriding your width
  },
}));

interface defaultSelectedFormDataProps {
  patientId: number;
  bsaId: string;
  firstName: string;
  lastName: string;
  unitNumber: string;
  campName: string;
  campId?: string;
}

interface CreateEMSSceneDialogProps {
  isOpen: boolean;
  formData?: defaultSelectedFormDataProps;
  dialogHeading: string;
  comesFrom?: string;
  handleClose: () => void;
  handleSubmittedData?: (data: any, selectedVisitType: string) => void;
}

export default function CreateEMSSceneDialog({
  isOpen = false,
  comesFrom,
  formData,
  handleSubmittedData,
  handleClose,
  dialogHeading = "Your Heading Here...",
}: CreateEMSSceneDialogProps) {
  const headerStyle = {
    fontWeight: "500",
    fontSize: "16px",
    paddingBottom: "10px",
  };
  const { patientIdFromParticipant } = useSelector(
    (state: any) => state[componentKey]
  );

  const dispatch = useDispatch();
  const [pageCount, setPageCount] = React.useState(1);
  const [dropdownSearch, setDropdownSearch] = React.useState("");
  const [dropdownOptions, setDropdownOptions] = React.useState<any>([]);
  const [selectedValue, setSelectedValue] = React.useState(null);
  const [resetFlag, setResetFlag] = React.useState(false);
  const [timer, setTimer] = React.useState<NodeJS.Timeout | null>(null);

  const validationSchema = Yup.object().shape({
    [CREATE_EMS_SCENE.CHIEF_COMPLAINT]: Yup.string().required(
      "Chief Complaint is required"
    ),
    [CREATE_EMS_SCENE?.PATIENT_NAME]: Yup.string().required(
      "Patient Name is required"
    ),
  });

  const dateTimeStyle = {
    fontWeight: "500",
    fontSize: "16px",
    paddingBottom: "15px",
    color: "rgb(88, 100, 103)",
  };

  const commonGridStyle = {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "14px",
  };

  React.useEffect(() => {
    if (!resetFlag) {
      if (dropdownSearch && dropdownSearch.length > 0) {
        getDropdownSearchList(dropdownSearch);
      } else {
        getDropdownList(pageCount);
      }
    }
  }, [dropdownSearch, pageCount]);

  React.useEffect(() => {
    if (resetFlag) {
      resetList();
    }
  }, [resetFlag]);

  const resetList = () => {
    setSelectedValue(null);
    setDropdownSearch("");
    setPageCount(1);
    getDropdownList(1, true);
  };

  // IF SEARCH VALUE  THEN WE ARE GIVEN SOME TIMER
  const handleSearchedValueChange = (data: any) => {
    if (timer) {
      clearTimeout(timer); // Clear the previous timer
    }
    // Set a new timer with your desired delay (e.g., 500ms)
    const newTimer = setTimeout(() => {
      setDropdownSearch(data);
    }, 500); // Adjust the delay as needed (in milliseconds)

    setTimer(newTimer); // Store the timer ID for cleanup
  };

  // Map a participant record to a dropdown option (shared by browse + search).
  const mapParticipantOption = (data: any) => ({
    label: `${data?.firstName || data?.lastName ? data?.firstName + " " + data?.lastName : "User"}`,
    value: `${data?.id}`,
    bsaId: data?.bsaId,
    clinicLocation: data?.baseCamp?.name,
    jun: data?.baseCamp?.unitNumber,
  });

  // Patients per browse page (infinite scroll appends the next page).
  const PATIENT_PAGE_LIMIT = 20;

  // GET DROPDOWN LIST (browse) — paginated, appends on scroll-to-bottom.
  const getDropdownList = async (page: number, reset = false) => {
    dispatch(showLoader());
    try {
      const response = await getLazyLoadingApiResponse(
        "participant",
        page,
        PATIENT_PAGE_LIMIT
      );
      const participantList = response?.data?.participant || [];
      const mappedPatientData = participantList.map(mapParticipantOption);
      if (mappedPatientData.length > 0) {
        if (reset) {
          setDropdownOptions(mappedPatientData);
          setResetFlag(false);
        } else {
          setDropdownOptions((prev: any) =>
            prev?.some((o: any) => o?.value === mappedPatientData[0]?.value)
              ? prev
              : (prev || []).concat(mappedPatientData)
          );
        }
      } else if (reset) {
        setDropdownOptions([]);
        setResetFlag(false);
      }
    } catch (error) {
      console.log("error:", error);
    } finally {
      dispatch(hideLoader());
    }
  };

  // GET DROPDOWN SEARCH LIST
  const getDropdownSearchList = async (search: string) => {
    dispatch(showLoader());
    try {
      const response = await getLazyLoadingSearchApiResponse(
        "participant",
        search
      );
      const participantList = response?.data?.participant || {};
      const mappedPatientData = participantList?.map(mapParticipantOption);
      if (mappedPatientData?.length) {
        setDropdownOptions(mappedPatientData);
      }
    } catch (error) {
      console.log("error:", error);
    } finally {
      dispatch(hideLoader());
    }
  };

  const handleVisitClick = (data: any) => {
    const payload = {
      participantId: data?.patientName || undefined,
      injuryType: data?.injuryType || undefined,
      injuryLocation: data?.injuryLocation || undefined,
      chiefComplaints: data?.chiefComplaint || undefined,
      incidentDate: moment()?.format("YYYY-MM-DD"),
      incidentTime: moment().format("HH:mm"),
      incidentlocation: data?.incidentLocation || undefined,
      witnessName: data?.witnessName || undefined,
    };
    dispatch(createEMSVisit(payload));
  };

  return (
    <React.Fragment>
      <BootstrapDialog
        onClose={(event, reason) => {
          if (reason !== "backdropClick") {
            handleClose();
          }
        }}
        aria-labelledby="customized-dialog-title"
        open={isOpen}
      >
        <div
          className="flex justify-between items-center"
          style={{ margin: "14px 24px" }}
        >
          <Heading title={dialogHeading} />
          <div onClick={handleClose} className="cursor-pointer">
            <CloseIcon />
          </div>
        </div>

        <DialogContent dividers style={{ padding: "16px 24px 24px 24px" }}>
          <Formik
            initialValues={{
              [CREATE_EMS_SCENE.PATIENT_NAME]: "",
              [CREATE_EMS_SCENE.PATIENT_BSA_ID]: "",
              [CREATE_EMS_SCENE.CAMP_NAME]: "",
              [CREATE_EMS_SCENE.JUN]: "",
              [CREATE_EMS_SCENE.CHIEF_COMPLAINT]: "",
              [CREATE_EMS_SCENE.INJURY_TYPE]: "",
              [CREATE_EMS_SCENE.INJURY_LOCATION]: "",
              [CREATE_EMS_SCENE.CAUSE_OF_INJURY_NAME]: "",
              [CREATE_EMS_SCENE.WITNESS_NAME]: "",
              [CREATE_EMS_SCENE.INCIDENT_LOCATION]: "",
              [CREATE_EMS_SCENE.TYPE_OF_CARE]: "",
              [CREATE_EMS_SCENE.FACILITY]: "",
            }}
            validationSchema={validationSchema}
            //   validateOnChange={false}
            //   validateOnBlur={false}
            enableReinitialize
            onSubmit={(values, { setSubmitting }) => {
              try {
                handleVisitClick(values);
                //   handleOnSubmittedData && handleOnSubmittedData(values);
                // Handle form data here
                // If needed, call an API or further process the values here
              } catch (error) {
                console.log(error);
              } finally {
                setSubmitting(false); // Reset submitting state
              }
            }}
          >
            {({
              errors,
              submitForm,
              isSubmitting,
              handleReset,
              isValid,
              values,
              touched,
              setFieldValue,
              handleBlur,
              handleSubmit,
            }) => {
              return (
                <Form
                  onSubmit={handleSubmit}
                  className="h-full flex flex-col justify-between"
                >
                  <div className="w-full flex flex-col gap-6">
                    {/* PATIENT DETAILS */}
                    <div>
                      <Heading
                        title="Patient Details"
                        customStyle={headerStyle}
                      />
                      <div style={commonGridStyle}>
                        <EventEHRLazyDropdown
                          // label="Search"
                          placeholder="Search Patient"
                          startIcon={
                            <img
                              alt="search-icon"
                              src={searchIcon}
                              width={"18px"}
                              height={"18px"}
                            />
                          }
                          onBlur={handleBlur}
                          error={
                            touched[CREATE_EMS_SCENE?.PATIENT_NAME] &&
                            Boolean(errors[CREATE_EMS_SCENE?.PATIENT_NAME])
                          }
                          helperText={
                            touched[CREATE_EMS_SCENE?.PATIENT_NAME]
                              ? errors[CREATE_EMS_SCENE?.PATIENT_NAME]
                              : undefined
                          }
                          noOptionText="Search Patient"
                          dropdownOptions={dropdownOptions}
                          dropdownSearch={dropdownSearch}
                          selectedValue={selectedValue}
                          setDropdownSearch={handleSearchedValueChange}
                          setPageCount={(data) => {
                            setPageCount(data);
                          }}
                          setResetFlag={(data) => {
                            setResetFlag(data);
                          }}
                          setSelectedValue={(data) => {
                            setFieldValue(
                              CREATE_EMS_SCENE?.PATIENT_NAME,
                              data?.value ? data?.value : ""
                            );
                            setFieldValue(
                              CREATE_EMS_SCENE?.PATIENT_BSA_ID,
                              data?.bsaId
                            );
                            setFieldValue(
                              CREATE_EMS_SCENE?.CAMP_NAME,
                              data?.clinicLocation
                            );
                            setFieldValue(CREATE_EMS_SCENE?.JUN, data?.jun);
                            setSelectedValue(data);
                          }}
                        />

                        <Input
                          disabled={true}
                          textFieldLabel="BSA ID"
                          name={CREATE_EMS_SCENE.PATIENT_BSA_ID}
                          value={values[CREATE_EMS_SCENE.PATIENT_BSA_ID]}
                          onChangeCb={(e) => {
                            setFieldValue(
                              CREATE_EMS_SCENE.PATIENT_BSA_ID,
                              e.target.value
                            );
                          }}
                          size="small"
                          onBlur={handleBlur}
                          error={
                            touched[CREATE_EMS_SCENE.PATIENT_BSA_ID] &&
                            Boolean(errors[CREATE_EMS_SCENE.PATIENT_BSA_ID])
                          }
                          helperText={
                            touched[CREATE_EMS_SCENE.PATIENT_BSA_ID]
                              ? errors[CREATE_EMS_SCENE.PATIENT_BSA_ID]
                              : undefined
                          }
                        />

                        <Input
                          disabled={true}
                          textFieldLabel="Jamboree Unit Number"
                          name={CREATE_EMS_SCENE.JUN}
                          value={values[CREATE_EMS_SCENE.JUN]}
                          onChangeCb={(e) => {
                            setFieldValue(CREATE_EMS_SCENE.JUN, e.target.value);
                          }}
                          onBlur={handleBlur}
                          size="small"
                          error={
                            touched[CREATE_EMS_SCENE.JUN] &&
                            Boolean(errors[CREATE_EMS_SCENE.JUN])
                          }
                          helperText={
                            touched[CREATE_EMS_SCENE.JUN]
                              ? errors[CREATE_EMS_SCENE.JUN]
                              : undefined
                          }
                        />
                      </div>
                    </div>

                    {/* CHIEF COMPLAINT SECTION */}
                    <div>
                      <Heading
                        title="Chief Complaint"
                        isRequired={true}
                        customStyle={headerStyle}
                      />
                      <div>
                        <Input
                          multiline={true}
                          rows={3}
                          textFieldLabel="Enter Chief Complaint"
                          maxWords={250}
                          name={CREATE_EMS_SCENE.CHIEF_COMPLAINT}
                          value={values[CREATE_EMS_SCENE.CHIEF_COMPLAINT]}
                          onChangeCb={(e) => {
                            setFieldValue(
                              CREATE_EMS_SCENE.CHIEF_COMPLAINT,
                              e.target.value
                            );
                          }}
                          onBlur={handleBlur}
                          size="small"
                          error={
                            touched[CREATE_EMS_SCENE.CHIEF_COMPLAINT] &&
                            Boolean(errors[CREATE_EMS_SCENE.CHIEF_COMPLAINT])
                          }
                          helperText={
                            touched[CREATE_EMS_SCENE.CHIEF_COMPLAINT]
                              ? errors[CREATE_EMS_SCENE.CHIEF_COMPLAINT]
                              : undefined
                          }
                        />
                      </div>
                    </div>

                    {/* Injury DETAILS */}
                    <div>
                      <Heading
                        title="Incident Details"
                        customStyle={headerStyle}
                      />
                      <div className="flex gap-2 text-[#727272]">
                        <Heading
                          title="Incident Date & Time :"
                          customStyle={dateTimeStyle}
                        />
                        {moment().format("MMMM DD YYYY, HH:mm")}
                      </div>

                      <div
                        style={{
                          ...commonGridStyle,
                          rowGap: "21px",
                          columnGap: "16px",
                        }}
                      >
                        <Input
                          textFieldLabel="Injury Type"
                          //   isStarPresent={true}
                          maxLength={100}
                          name={CREATE_EMS_SCENE.INJURY_TYPE}
                          value={values[CREATE_EMS_SCENE.INJURY_TYPE]}
                          onChangeCb={(e) => {
                            setFieldValue(
                              CREATE_EMS_SCENE.INJURY_TYPE,
                              e.target.value
                            );
                          }}
                          size="small"
                          onBlur={handleBlur}
                          error={
                            touched[CREATE_EMS_SCENE.INJURY_TYPE] &&
                            Boolean(errors[CREATE_EMS_SCENE.INJURY_TYPE])
                          }
                          helperText={
                            touched[CREATE_EMS_SCENE.INJURY_TYPE]
                              ? errors[CREATE_EMS_SCENE.INJURY_TYPE]
                              : undefined
                          }
                        />

                        <Input
                          textFieldLabel="Injury Location"
                          //   isStarPresent={true}
                          maxLength={100}
                          name={CREATE_EMS_SCENE.INJURY_LOCATION}
                          value={values[CREATE_EMS_SCENE.INJURY_LOCATION]}
                          onChangeCb={(e) => {
                            setFieldValue(
                              CREATE_EMS_SCENE.INJURY_LOCATION,
                              e.target.value
                            );
                          }}
                          size="small"
                          onBlur={handleBlur}
                          error={
                            touched[CREATE_EMS_SCENE.INJURY_LOCATION] &&
                            Boolean(errors[CREATE_EMS_SCENE.INJURY_LOCATION])
                          }
                          helperText={
                            touched[CREATE_EMS_SCENE.INJURY_LOCATION]
                              ? errors[CREATE_EMS_SCENE.INJURY_LOCATION]
                              : undefined
                          }
                        />
                        <Input
                          textFieldLabel="Witness Name"
                          //   isStarPresent={true}
                          maxLength={100}
                          name={CREATE_EMS_SCENE.WITNESS_NAME}
                          value={values[CREATE_EMS_SCENE.WITNESS_NAME]}
                          onChangeCb={(e) => {
                            setFieldValue(
                              CREATE_EMS_SCENE.WITNESS_NAME,
                              e.target.value
                            );
                          }}
                          size="small"
                          onBlur={handleBlur}
                          error={
                            touched[CREATE_EMS_SCENE.WITNESS_NAME] &&
                            Boolean(errors[CREATE_EMS_SCENE.WITNESS_NAME])
                          }
                          helperText={
                            touched[CREATE_EMS_SCENE.WITNESS_NAME]
                              ? errors[CREATE_EMS_SCENE.WITNESS_NAME]
                              : undefined
                          }
                        />

                        <Input
                          textFieldLabel="Incident Location"
                          //   isStarPresent={true}
                          maxLength={100}
                          name={CREATE_EMS_SCENE.INCIDENT_LOCATION}
                          value={values[CREATE_EMS_SCENE.INCIDENT_LOCATION]}
                          onChangeCb={(e) => {
                            setFieldValue(
                              CREATE_EMS_SCENE.INCIDENT_LOCATION,
                              e.target.value
                            );
                          }}
                          size="small"
                          onBlur={handleBlur}
                          error={
                            touched[CREATE_EMS_SCENE.INCIDENT_LOCATION] &&
                            Boolean(errors[CREATE_EMS_SCENE.INCIDENT_LOCATION])
                          }
                          helperText={
                            touched[CREATE_EMS_SCENE.INCIDENT_LOCATION]
                              ? errors[CREATE_EMS_SCENE.INCIDENT_LOCATION]
                              : undefined
                          }
                        />
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-end mt-10">
                    <Button
                      // disabled={!isValid} // Button disabled if form is invalid or submitting
                      type="submit"
                      label={"Transfer"}
                      variant="contained"
                      onClickCb={() => {}}
                      style={{
                        padding: "8px 55px",
                        fontSize: "16px",
                      }}
                    ></Button>
                  </div>
                </Form>
              );
            }}
          </Formik>
        </DialogContent>
      </BootstrapDialog>
    </React.Fragment>
  );
}
